// Lightweight singleton dispatcher for the "bulk checklist insert" confirmation dialog.
// A host component (mounted once at app root) subscribes via `subscribeBulkConfirm`
// and resolves the pending request when the user clicks confirm/cancel.

export interface BulkConfirmChild {
  label: string;
  due_date?: string | null;
}

export interface BulkConfirmRequest {
  id: number;
  checklistTitle: string;
  parentLabel: string;
  parentDueDate?: string | null;
  children: BulkConfirmChild[];
  resolve: (confirmed: boolean) => void;
}

type Listener = (req: BulkConfirmRequest | null) => void;

let listener: Listener | null = null;
let nextId = 1;
let current: BulkConfirmRequest | null = null;

export function subscribeBulkConfirm(fn: Listener): () => void {
  listener = fn;
  if (current) fn(current);
  return () => {
    if (listener === fn) listener = null;
  };
}

export function requestBulkConfirm(
  payload: Omit<BulkConfirmRequest, "id" | "resolve">
): Promise<boolean> {
  // If a previous request is still pending (no host mounted), auto-cancel it.
  if (current) {
    current.resolve(false);
    current = null;
  }
  return new Promise<boolean>((resolve) => {
    current = {
      ...payload,
      id: nextId++,
      resolve: (v) => {
        current = null;
        listener?.(null);
        resolve(v);
      },
    };
    if (listener) {
      listener(current);
    } else {
      // No host mounted — fail-safe: skip confirmation rather than block forever.
      console.warn("[bulkConfirm] No host mounted, auto-confirming bulk insert.");
      const c = current;
      current = null;
      resolve(true);
      void c;
    }
  });
}
